import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import SearchableSelect from '../SearchableSelect';
import { adminApi } from '../../lib/adminApi';
import { useData } from '../../context/DataContext';
import { UNITS, slugify } from './adminHelpers';

const AdminProductForm = ({ initial, onSave, onCancel }) => {
  const { tree } = useData();
  const isUpdate = !!initial?.id;
  const [vendors, setVendors] = useState([]);
  const [v, setV] = useState(() => ({
    name: initial?.name || '',
    slug: initial?.slug || '',
    price: initial?.price || '',
    unit: initial?.unit || 'Piece',
    moq: initial?.moq || '1',
    image: initial?.image || '',
    gst: initial?.gst || 'GST Verified',
    description: initial?.description || '',
    supplier_id: initial?.supplier_id || '',
    main_category_id: '',
    category_id: '',
    sub_category_id: initial?.sub_category_id || '',
    verified: initial?.verified !== false,
  }));
  const set = (k, val) => setV((prev) => ({ ...prev, [k]: val }));

  useEffect(() => {
    adminApi.list('suppliers').then((d) => setVendors(d || [])).catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    if (!tree.length || !v.sub_category_id || v.category_id) return;
    for (const m of tree) {
      for (const c of (m.categories || [])) {
        if ((c.sub_categories || []).some((s) => s.id === v.sub_category_id)) {
          setV((prev) => ({ ...prev, main_category_id: m.id, category_id: c.id }));
          return;
        }
      }
    }
  }, [tree, v.sub_category_id, v.category_id]);

  const mainCategories = tree;
  const categories = (tree.find((m) => m.id === v.main_category_id)?.categories) || [];
  const subCategories = (categories.find((c) => c.id === v.category_id)?.sub_categories) || [];

  const onNameChange = (val) => {
    setV((prev) => {
      const auto = prev.slug === '' || prev.slug === slugify(prev.name);
      return { ...prev, name: val, slug: auto ? slugify(val) : prev.slug };
    });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!v.supplier_id) return alert('Please select a vendor');
    if (!v.sub_category_id) return alert('Please select a sub-category');
    if (!v.image) return alert('Please provide an image URL');
    const payload = {
      ...v,
      price: Number(v.price) || 0,
      images: v.image ? [v.image] : [],
    };
    delete payload.main_category_id;
    delete payload.category_id;
    onSave(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-product-form">
      <div>
        <h3 className="text-sm font-semibold mb-2 text-[#2c54b2]">Vendor & Category</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Vendor <span className="text-red-500">*</span></Label>
            <SearchableSelect
              options={vendors.map((s) => ({
                id: s.id, name: s.name,
                hint: `${s.city ? s.city.split(',')[0] : ''}${s.status === 'inactive' ? ' · inactive' : ''}`,
              }))}
              value={v.supplier_id}
              onChange={(id) => set('supplier_id', id)}
              required
              placeholder="— Search & select vendor —"
              testId="product-vendor-select"
            />
          </div>
          <div>
            <Label className="text-xs">Main Category <span className="text-red-500">*</span></Label>
            <SearchableSelect
              options={mainCategories.map((m) => ({ id: m.id, name: m.name }))}
              value={v.main_category_id}
              onChange={(id) => setV((prev) => ({ ...prev, main_category_id: id, category_id: '', sub_category_id: '' }))}
              required
              placeholder="— Search main category —"
              testId="product-main-cat-select"
            />
          </div>
          <div>
            <Label className="text-xs">Category <span className="text-red-500">*</span></Label>
            <SearchableSelect
              options={categories.map((c) => ({ id: c.id, name: c.name }))}
              value={v.category_id}
              onChange={(id) => setV((prev) => ({ ...prev, category_id: id, sub_category_id: '' }))}
              required
              disabled={!v.main_category_id}
              placeholder={v.main_category_id ? '— Search category —' : 'Select main category first'}
              testId="product-cat-select"
            />
          </div>
          <div>
            <Label className="text-xs">Sub-category <span className="text-red-500">*</span></Label>
            <SearchableSelect
              options={subCategories.map((s) => ({ id: s.id, name: s.name }))}
              value={v.sub_category_id}
              onChange={(id) => set('sub_category_id', id)}
              required
              disabled={!v.category_id}
              placeholder={v.category_id ? '— Search sub-category —' : 'Select category first'}
              testId="product-subcat-select"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2 text-[#2c54b2]">Product Details</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Product Name <span className="text-red-500">*</span></Label>
            <Input value={v.name} onChange={(e) => onNameChange(e.target.value)} required placeholder="e.g. MS Round Steel Pipe 40mm" data-testid="product-name" />
          </div>
          <div>
            <Label className="text-xs">Slug</Label>
            <Input value={v.slug} onChange={(e) => set('slug', e.target.value)} placeholder="auto-generated from name" />
          </div>
          <div>
            <Label className="text-xs">Price (₹) <span className="text-red-500">*</span></Label>
            <Input type="number" min="0" step="0.01" value={v.price} onChange={(e) => set('price', e.target.value)} required data-testid="product-price" />
          </div>
          <div>
            <Label className="text-xs">Unit</Label>
            <select value={v.unit} onChange={(e) => set('unit', e.target.value)} className="w-full h-10 text-sm border border-gray-300 rounded px-2 focus:outline-none focus:border-[#ee5e23]">
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Min. Order Qty (MOQ)</Label>
            <Input value={v.moq} onChange={(e) => set('moq', e.target.value)} placeholder="e.g. 100" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Product Image <span className="text-red-500">*</span></Label>
            <div className="flex items-start gap-3 mt-1">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    set('_uploading', true);
                    try {
                      const r = await adminApi.uploadImage(f, 'product');
                      const base = process.env.REACT_APP_BACKEND_URL || '';
                      set('image', r.url.startsWith('http') ? r.url : `${base}${r.url}`);
                    } catch (err) {
                      alert(err?.response?.data?.detail || 'Upload failed');
                    } finally {
                      set('_uploading', false);
                    }
                  }}
                  className="text-sm block w-full border border-gray-300 rounded px-2 py-2 bg-white"
                  data-testid="product-image-upload"
                />
                <div className="text-[11px] text-gray-500 mt-1">PNG, JPG, WEBP or GIF · up to 5 MB</div>
                {v._uploading && <div className="text-xs text-[#ee5e23] mt-1">Uploading…</div>}
                <Input
                  value={v.image}
                  onChange={(e) => set('image', e.target.value)}
                  placeholder="…or paste an image URL"
                  className="mt-2"
                  data-testid="product-image"
                />
              </div>
              {v.image && (
                <img
                  src={v.image}
                  alt="preview"
                  className="w-24 h-24 object-cover rounded border bg-white"
                  onError={(e) => { e.target.style.opacity = '0.3'; }}
                  data-testid="product-image-preview"
                />
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">GST Status</Label>
            <select value={v.gst} onChange={(e) => set('gst', e.target.value)} className="w-full h-10 text-sm border border-gray-300 rounded px-2 focus:outline-none focus:border-[#ee5e23]">
              <option>GST Verified</option>
              <option>Pending Verification</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={v.verified} onChange={(e) => set('verified', e.target.checked)} />
              Verified listing
            </label>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Description</Label>
            <Textarea rows={3} value={v.description} onChange={(e) => set('description', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="product-save-btn">
          {isUpdate ? 'Save Changes' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

export default AdminProductForm;
